import React, { type FC, type PropsWithChildren } from 'react'
import { GlobalStyles, type Theme, ThemeProvider } from '@mui/material'
import { SnackbarProvider } from 'notistack'

import { ErrorBoundary } from './ErrorMessage'
import { TranslationProvider, type TranslationProviderProps } from './Internationalization'
import { ToastSnackbarContent } from './Notifications'
import { type AppStateProps, AppStateProvider } from './State'
import { notistackDefaultBottomOffsetPx } from './common/Notifications'
import { DEVIATION_ZONE_HEIGHT_CSS_VAR } from './DeviationNotifications/ToastZone'

const snackbarComponents = { toast: ToastSnackbarContent }

const GENERAL_TOAST_ZONE_OFFSET_CLASS = 'connectware-general-toast-zone-offset'

/** This theme's spacing unit is 4px, so `4` = 16px, the gap Figma specifies between the two toast zones. */
const DEVIATION_ZONE_GAP_SPACING_STEPS = 4

/** Shifts the general Toast zone's container up by the deviation zone's published height plus the gap, so the two never overlap. */
const generalToastZoneOffsetStyles = (theme: Theme): Record<string, Record<string, string>> => ({
    [`.${GENERAL_TOAST_ZONE_OFFSET_CLASS}`]: {
        bottom: `calc(${notistackDefaultBottomOffsetPx}px + var(${DEVIATION_ZONE_HEIGHT_CSS_VAR}, 0px) + ${
            theme.spacing(
                DEVIATION_ZONE_GAP_SPACING_STEPS
            )
        }) !important`,
    },
})

const snackbarProviderClasses = { containerAnchorOriginBottomRight: GENERAL_TOAST_ZONE_OFFSET_CLASS }

/**
 * `ToastUsecase.applyCaps` excludes `persist: true` toasts from its own max-3 cap (a persist toast,
 * e.g. `PermissionsReducedModal`'s reminder, isn't capped by count), so notistack's own `maxSnack`
 * (default 3) still needs enough headroom for the general cap plus at least one persist toast, or
 * notistack would evict it once enough other toasts arrive - `ToastPortal` has no way to tell
 * notistack a given snack is exempt. Matches this app's original notistack config.
 */
const MAX_SNACK = 5

/**
 * notistack's own default anchor is bottom-*left* - never overridden until now, so the general Toast
 * zone has been rendering there regardless of Figma's "anchored bottom-right" spec (caught live during
 * CC-4855's QA pass, checking the two-zone layout coordination - a pre-existing gap this ticket
 * depends on, not something CC-4855 introduced). Explicit here so `containerAnchorOriginBottomRight`
 * (the class the deviation-zone-offset override above targets) actually matches where toasts render.
 */
const TOAST_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'right' } as const

export type ReactAppInjections = TranslationProviderProps & AppStateProps & Readonly<{ theme: Theme }>

export const AppWrapper: FC<PropsWithChildren<ReactAppInjections>> = ({ internationalization, manager, usecases, children, theme }) => (
    <AppStateProvider manager={manager} usecases={usecases}>
        <TranslationProvider internationalization={internationalization}>
            <ThemeProvider theme={theme}>
                <GlobalStyles styles={generalToastZoneOffsetStyles} />
                <SnackbarProvider
                    Components={snackbarComponents}
                    classes={snackbarProviderClasses}
                    maxSnack={MAX_SNACK}
                    preventDuplicate
                    anchorOrigin={TOAST_ANCHOR_ORIGIN}
                >
                    <ErrorBoundary>{children}</ErrorBoundary>
                </SnackbarProvider>
            </ThemeProvider>
        </TranslationProvider>
    </AppStateProvider>
)
